import * as React from 'react'

import { CameraIcon } from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { classList } from 'react-classlist-helper'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  FilePicker,
  IFilePickerActions,
} from '~/client/src/shared/components/FilePicker'
import * as Icons from '~/client/src/shared/components/Icons'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import FileType from '../../enums/FileType'
import { NO_IMAGE_PATH } from '../../utils/usefulStrings'
import PdfDocument from '../PdfDocument/PdfDocument'
import PhotoThumbnail from '../PhotoThumbnail/PhotoThumbnail'
import IFileInputProps from './IFileInputProps'

export default class FileInputBase<
  T extends IFileInputProps,
> extends React.Component<T> {
  @observable public isImageLoaded: boolean = false
  private readonly filePicker = React.createRef<IFilePickerActions>()

  public constructor(props: T) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const { id, name, isReadonly, textClassName, className, shouldHideName } =
      this.props

    const isPdf = name.split('.')?.pop()?.toLowerCase() === FileType.Pdf

    return (
      <>
        <div className={className || ''}>
          {isPdf ? this.renderPdfPreview() : this.renderImagePreview()}
          {!isReadonly && this.renderChangeButton()}
        </div>
        {!shouldHideName && (
          <label className={textClassName} htmlFor={id}>
            {name}
          </label>
        )}
      </>
    )
  }

  private renderImagePreview() {
    const {
      id,
      value,
      thumbUrl,
      isFlexPreview,
      shouldUseFullSize,
      shouldHideIconAndOutline,
      onClickHandler,
    } = this.props

    const imageSrc = thumbUrl || value || NO_IMAGE_PATH

    return (
      <div
        id={id}
        className={classList({
          'file-preview tw:w-[150px] tw:h-[100px] tw:shrink-0 tw:grow-0 tw:object-contain tw:overflow-hidden tw:inline-block tw:relative': true,
          'tw:!w-full tw:!h-full': shouldUseFullSize,
          'tw:!w-full tw:!h-auto': isFlexPreview,
        })}
      >
        {!this.isImageLoaded && <PhotoThumbnail />}
        <img
          src={imageSrc}
          className={classList({
            'file-preview tw:w-[150px] tw:h-[100px] tw:shrink-0 tw:grow-0 tw:object-contain tw:overflow-hidden tw:inline-block tw:relative': true,
            'tw:border tw:border-solid tw:border-border':
              !shouldHideIconAndOutline,
            'tw:!w-full tw:!h-full': shouldUseFullSize,
            'tw:!w-full tw:!h-auto': isFlexPreview,
          })}
          style={!this.isImageLoaded ? { visibility: 'hidden' } : {}}
          onLoad={this.setLoaded}
          onClick={onClickHandler || this.showFullscreenPreview}
        />
        {!shouldHideIconAndOutline && (
          <span className="tw:absolute tw:-bottom-px tw:-left-px tw:bg-card tw:flex tw:items-center tw:justify-center tw:h-[25px] tw:w-[30px] tw:border tw:border-solid tw:border-border">
            <CameraIcon size={16} className="tw:text-muted-foreground" />
          </span>
        )}
      </div>
    )
  }

  private renderPdfPreview() {
    const { id, value, shouldUseFullSize } = this.props
    return (
      <a
        id={id}
        href={value}
        target="_blank"
        title={Localization.translator.download}
        className={classList({
          'file-preview tw:w-[150px] tw:h-[100px] tw:shrink-0 tw:grow-0 tw:object-contain tw:overflow-hidden tw:inline-block tw:relative tw:border tw:border-solid tw:border-border': true,
          'tw:!w-full tw:!h-full': shouldUseFullSize,
        })}
      >
        <PdfDocument file={value} />
        <Icons.Pdf className="tw:absolute tw:-bottom-px tw:-left-px tw:bg-card tw:flex tw:items-center tw:justify-center tw:h-[35px] tw:w-[55px] tw:border tw:border-solid tw:border-border" />
      </a>
    )
  }

  private renderChangeButton() {
    return (
      <FilePicker
        actionsRef={this.filePicker}
        accept="application/pdf, image/*"
        className="tw:flex-none!"
        onPick={this.uploadFile}
        render={<Button variant="link" />}
      >
        {Localization.translator.change_verb}
      </FilePicker>
    )
  }

  protected promptForFile() {
    this.filePicker.current?.open()
  }

  protected stopPropagation(event) {
    if (event) {
      event.stopPropagation()
    }
  }

  private uploadFile = async ([file]: File[]) => {
    const { fileUploadingStore } = this.props

    const [result] = await fileUploadingStore.uploadFile(
      file,
      fileUploadingStore.getAttachmentFileType(file),
      this.props.projectId,
    )
    if (!result) {
      return
    }

    this.props.onChange(result.fileURL, file.name)
  }

  protected showFullscreenPreview(event) {
    this.stopPropagation(event)
  }

  @action.bound
  private setLoaded() {
    this.isImageLoaded = true
  }
}
